import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const empty = { name: "", email: "", phone: "", company: "", subject: "", message: "" };

const MAP_SRC =
  "https://www.google.com/maps?q=" +
  encodeURIComponent("Shri Ram Nagar, G.T. Road, Shahdara, Delhi 110032") +
  "&output=embed";

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    access_key: "e1e45e6d-428c-474b-a0b6-c67c260545bc",
    subject: form.subject,
    from_name: form.name,
    name: form.name,
    email: form.email,
    phone: form.phone,
    company: form.company,
    message: form.message,
  }),
});

const result = await response.json();

console.log(result);

if (!result.success) {
  throw new Error(result.message || "Submission failed");
}
      toast.success("Thank you! Your message has been received.");
      setForm(empty);
    } catch (err) {
  toast.error(err.message || "Submission failed.");
    } finally {
  setLoading(false);
    }
  };

  const inputCls =
    "bg-background border-input focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-12 rounded-lg text-foreground placeholder:text-muted-foreground/60";

  return (
    <div className="bg-background">
      <section className="relative mx-auto max-w-[1400px] px-6 pb-16 pt-40 lg:px-10 lg:pt-48 grain">
        <p className="text-xs uppercase tracking-[0.25em] text-gold">Contact & Feedback</p>
        <h1 className="mt-6 font-serif-display text-5xl font-semibold leading-[0.98] tracking-tight text-foreground md:text-6xl lg:text-7xl">
          <MaskedLines lines={["Get in touch", "with us."]} />
        </h1>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-14 px-6 pb-24 lg:grid-cols-[0.85fr_1.15fr] lg:px-10">
        {/* Info side */}
        <Reveal>
          <div className="space-y-8">
            <p className="max-w-md text-lg leading-relaxed text-muted-foreground">
              Share your formulation or scintigraphy challenge. Our scientific team reviews every
              enquiry and responds with a tailored solution package. Submissions reach us at
              info@hypothoresearch.com.
            </p>
            <div className="space-y-6">
              <a href="tel:+919015893925" className="group flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-primary transition-colors duration-300 group-hover:border-primary">
                  <Phone className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-gold">Phone</p>
                  <p className="mt-1 text-foreground">+91 90158 93925</p>
                </div>
              </a>
              <a href="mailto:info@hypothoresearch.com" className="group flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-primary transition-colors duration-300 group-hover:border-primary">
                  <Mail className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-gold">Email</p>
                  <p className="mt-1 text-foreground">info@hypothoresearch.com</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-primary">
                  <MapPin className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-gold">Corporate Office</p>
                  <p className="mt-1 max-w-xs leading-relaxed text-foreground">
                    4/25, First Floor, G.T. Road, Shri Ram Nagar, Near UCO Bank, Shahdara, Delhi — 110032
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-border shadow-luxe" data-testid="office-map">
              <iframe
                title="Hypotho Research office location"
                src={MAP_SRC}
                width="100%"
                height="260"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.15}>
          <motion.form
            onSubmit={submit}
            data-testid="feedback-form"
            className="rounded-2xl border border-border bg-card p-7 shadow-luxe-lg lg:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full Name *</Label>
                <Input id="name" data-testid="feedback-name" value={form.name} onChange={set("name")} placeholder="Dr. Jane Doe" className={inputCls} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email Address *</Label>
                <Input id="email" type="email" data-testid="feedback-email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone Number</Label>
                <Input id="phone" data-testid="feedback-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Company / Organization</Label>
                <Input id="company" data-testid="feedback-company" value={form.company} onChange={set("company")} placeholder="Your organization" className={inputCls} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="subject" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Subject *</Label>
                <Input id="subject" data-testid="feedback-subject" value={form.subject} onChange={set("subject")} placeholder="How can we help?" className={inputCls} required />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="message" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Message *</Label>
                <Textarea id="message" data-testid="feedback-message" value={form.message} onChange={set("message")} placeholder="Describe your project or query..." rows={5} className="bg-background border-input focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary rounded-lg text-foreground placeholder:text-muted-foreground/60" required />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="feedback-submit"
              className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 font-semibold text-primary-foreground transition-colors duration-300 hover:bg-gold disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5" />}
              {loading ? "Sending..." : "Send Message"}
            </button>
          </motion.form>
        </Reveal>
      </section>
    </div>
  );
}
